/** Matches http(s) URLs in free text. Excludes whitespace and common
 *  closing delimiters so trailing punctuation isn't captured. */
const URL_REGEX = /\bhttps?:\/\/[^\s<>"')\]]+/gi;

/**
 * Build the ordered, deduped list of URLs to show in the detail sheet's
 * Links row: the item's own `link` first (when present), followed by any
 * http(s) URLs found in the free-text `notes`. Dedupe is case-insensitive
 * and keeps the first occurrence. Trailing punctuation is trimmed.
 */
export function extractUrls(
  link: string | null,
  notes: string | null,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string) => {
    const url = raw.replace(/[.,;:!?)\]]+$/, '').trim();
    if (!url) return;
    const key = url.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(url);
  };

  if (link && /^https?:\/\//i.test(link)) push(link);
  if (notes) {
    const matches = notes.match(URL_REGEX);
    if (matches) matches.forEach(push);
  }
  return out;
}

/** One stored link: an optional human label plus the URL. */
export type LinkEntry = { title: string | null; url: string };

/**
 * Auto-prepend `https://` when the URL has no http(s) scheme. Empty in →
 * empty out. The scheme check is case-insensitive and the original casing
 * is preserved (we only add a prefix, never rewrite).
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Adapt a legacy scalar `link` column into the array shape (back-compat). */
export function linksFromScalar(link: string | null | undefined): LinkEntry[] {
  return link && link.trim() ? [{ title: null, url: link.trim() }] : [];
}

/** Denormalized cache value written alongside `links` for back-compat reads. */
export function scalarFromLinks(links: LinkEntry[]): string | null {
  return links[0]?.url ?? null;
}

/**
 * The email body as prose, with every link removed (YAR-468).
 *
 * "View Source Reference" shows `emails.processed_content`, the text the
 * extractor read. The HTML cleaner keeps each anchor as `label (href)` so
 * the model can pick links up, and plain-text mail carries bare URLs — both
 * of which turn a school newsletter into something nobody wants to read.
 * The links are not lost: they live on the extracted items, and the
 * original message is one tap away in Gmail.
 *
 * Three passes, in this order:
 *   1. ` (http…)` / ` (mailto:…)` after a label → the label alone.
 *   2. Any remaining bare `http(s)://…` → gone (an anchor whose label was
 *      the URL itself is left with nothing, which is the intent).
 *   3. Lines that held only a URL are removed rather than left blank, and
 *      a space that now precedes punctuation is closed up.
 */
export function stripLinksForReading(text: string): string {
  // A URL may carry ONE level of balanced parentheses
  // (`/wiki/Fall_(season)`, `/flyer/(123)/view`). The old `[^\s()]*` stopped
  // at the first `(`, so the anchor pass missed the whole `(href)` and the
  // bare pass left `(season))` behind.
  const stripLine = (line: string) =>
    line
      .replace(/\s*\((?:https?:\/\/|mailto:)(?:[^\s()]|\([^\s()]*\))*\)/gi, '')
      .replace(/\s*\bhttps?:\/\/(?:[^\s<>"'()\]]|\([^\s()<>"']*\))+/gi, '')
      .replace(/[ \t]+([.,;:!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ');

  return text
    .split('\n')
    .flatMap((line) => {
      const stripped = stripLine(line);
      // A line that only held a link is dropped; a blank line the author
      // wrote (a paragraph break) is not.
      const emptiedByStrip = stripped.trim() === '' && line.trim() !== '';
      return emptiedByStrip ? [] : [stripped];
    })
    .join('\n');
}
